import {useEffect, useState} from 'react';
import {Button} from '@ui/buttons/button';
import {TextField} from '@ui/forms/input-field/text-field/text-field';
import {Trans} from '@ui/i18n/trans';
import {useTrans} from '@ui/i18n/use-trans';
import {message} from '@ui/i18n/message';
import ReactPhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import {useSendMobileOtp} from '../requests/use-send-mobile-otp';
import {useVerifyMobileOtp} from '../requests/use-verify-mobile-otp';

// otp codes expire 5min after being generated, see MobileAuthController::sendMobileLoginOtp
const OTP_LIFETIME_MS = 5 * 60 * 1000;

function useOtpCountdown() {
  const [expiresAt, setExpiresAt] = useState(() => Date.now() + OTP_LIFETIME_MS);
  const [remainingMs, setRemainingMs] = useState(() => expiresAt - Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      setRemainingMs(Math.max(0, expiresAt - Date.now()));
    }, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  const minutes = Math.floor(remainingMs / 60000);
  const seconds = Math.floor((remainingMs % 60000) / 1000);

  return {
    isExpired: remainingMs <= 0,
    formatted: `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`,
    reset: () => setExpiresAt(Date.now() + OTP_LIFETIME_MS),
  };
}

export function MobileOtpLoginForm() {
  const {trans} = useTrans();
  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [mobileNumber, setMobileNumber] = useState('');
  const [code, setCode] = useState('');
  const sendOtp = useSendMobileOtp();
  const verifyOtp = useVerifyMobileOtp();
  const otpCountdown = useOtpCountdown();

  if (step === 'phone') {
    return (
      <div>
        <label className="mb-30 block text-sm font-medium text-gray-100">
          <span className="mobile_label">
            <Trans message="Mobile Number" />
          </span>
          <div className="w-full mobile_number">
            <ReactPhoneInput
              value={mobileNumber}
              onChange={value => setMobileNumber(value)}
              country="in"
              countryCodeEditable={false}
            />
          </div>
        </label>
        {sendOtp.isError && (
          <div className="mb-16 text-sm text-red-600" style={{color: '#dc2626'}}>
            <Trans message="Could not send the code, please check the number and try again" />
          </div>
        )}
        <Button
          className="block w-full"
          variant="flat"
          color="primary"
          size="md"
          disabled={!mobileNumber || sendOtp.isPending}
          onClick={() => {
            sendOtp.mutate(mobileNumber, {
              onSuccess: () => {
                otpCountdown.reset();
                setStep('code');
              },
            });
          }}
        >
          <Trans message="Send code" />
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-16 text-sm text-muted">
        <Trans
          message="Code sent to :number"
          values={{number: `+${mobileNumber}`}}
        />
      </div>
      <TextField
        className="mb-8"
        value={code}
        onChange={e =>
          setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
        }
        label={<Trans message="Code" />}
        placeholder={trans(message('Enter your verification code'))}
        invalid={verifyOtp.isError}
        errorMessage={
          verifyOtp.isError
            ? trans(message('The code you entered is invalid or has expired'))
            : undefined
        }
        autoFocus
        autoComplete="one-time-code"
        autoCorrect="off"
        autoCapitalize="off"
        maxLength={6}
        inputMode="numeric"
        pattern="[0-9]*"
      />
      <div className="mb-16 text-sm text-muted">
        {otpCountdown.isExpired ? (
          <Trans message="Code has expired, please request a new one" />
        ) : (
          <Trans
            message="Code expires in :time"
            values={{time: otpCountdown.formatted}}
          />
        )}
      </div>
      <Button
        className="mb-8 block w-full"
        variant="flat"
        color="primary"
        size="md"
        disabled={code.length !== 6 || verifyOtp.isPending}
        onClick={() => {
          verifyOtp.mutate({mobile_number: mobileNumber, code});
        }}
      >
        <Trans message="Verify" />
      </Button>
      <div className="flex justify-between text-sm">
        <Button
          variant="text"
          size="xs"
          onClick={() => {
            setStep('phone');
            setCode('');
          }}
        >
          <Trans message="Change number" />
        </Button>
        <Button
          variant="text"
          size="xs"
          disabled={sendOtp.isPending}
          onClick={() => {
            sendOtp.mutate(mobileNumber, {
              onSuccess: () => otpCountdown.reset(),
            });
          }}
        >
          <Trans message="Resend code" />
        </Button>
      </div>
    </div>
  );
}
